import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"

export type PortfolioSectionHeaderProps = Omit<
  ComponentProps<"div">,
  "title"
> & {
  /** Small uppercase label over the title naming the chapter ("Activity"). */
  eyebrow?: ReactNode
  title: ReactNode
  /**
   * One or two plain sentences on what the section shows and how to read
   * it — written for a sales or CS colleague.
   */
  lead?: ReactNode
  /** Longer methodology behind a small info icon next to the title. */
  info?: string
}

// One header for every Portfolio section, in the report's grammar: an
// eyebrow, the title, a plain lead, and whatever controls the section puts
// on the right (children). No rule under it: the cards below carry the
// frame, the space between sections does the separating.
function PortfolioSectionHeader(props: PortfolioSectionHeaderProps) {
  const { eyebrow, title, lead, info, className, children, ...divProps } = props

  return (
    <div className={cn("usage-report-section-header", className)} {...divProps}>
      <div className="usage-report-section-heading">
        {eyebrow != null && (
          <span className="usage-report-eyebrow">{eyebrow}</span>
        )}
        <h3 className="usage-report-title flex items-center gap-1.5">
          {title}
          {info && (
            <TooltipIcon
              iconSrc="info"
              iconSize={13}
              iconClassName="shrink-0 text-muted-foreground/70"
              content={
                <span className="block max-w-[320px] px-3 py-2 text-xs font-normal leading-relaxed">
                  {info}
                </span>
              }
              contentSide="top"
              contentClassName="ui-tooltip-content--panel"
            />
          )}
        </h3>
        {lead != null && <p className="usage-report-lead">{lead}</p>}
      </div>
      {children != null && (
        <div className="usage-report-section-controls text-xs text-muted-foreground">
          {children}
        </div>
      )}
    </div>
  )
}

export default PortfolioSectionHeader
